import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import InnerImageZoom from 'react-inner-image-zoom';
import queryString from 'query-string';

import useScreen from 'hooks/useScreen';
import useGetCSSVariables from 'hooks/useGetCSSVariables';
import ImageSliderStandard from 'components/consumer/image-slider-standard';
import 'react-inner-image-zoom/lib/InnerImageZoom/styles.min.css';
import { EmblaSlide } from 'components/consumer/image-slider-standard/components/EmblaCarousel';
import isNewConsumerApp from 'commons/isNewConsumerApp';

const getSlideWidth = ({ isUltraWide, isExtraWide }) => {
  let result = 500;

  if (isUltraWide) {
    result = 620;
  } else if (isExtraWide) {
    result = 875;
  }
  return result;
};
const FabricGallery = ({
  slides = [],
  isMobileMaterialSwatches = false,
  bottomRightSide,
  topRightSide,
}) => {
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const { isDesktop, isUltraWide, isExtraWide, windowWidth } = useScreen();
  const [isZoomIn, setIsZoomIn] = useState(false);
  const slideWidthInitial = getSlideWidth({
    isUltraWide,
    isExtraWide,
  });

  const [slideWidth, setSlideWidth] = useState(slideWidthInitial);
  const {
    '--push-across-sidebar-open-width': pushAcrossSidebarOpenWidth = '1px',
  } = useGetCSSVariables(['--push-across-sidebar-open-width']);

  useEffect(() => {
    setSelectedSlideIndex(0);
  }, [slides]);

  useEffect(() => {
    if (pushAcrossSidebarOpenWidth === '1px') {
      return;
    }
    const slideWidthNew = parseInt(
      pushAcrossSidebarOpenWidth.replace('px', ''),
      10
    );
    if (slideWidth !== slideWidthNew && slideWidthNew) {
      setSlideWidth(slideWidthNew);
    }
  }, [pushAcrossSidebarOpenWidth, slideWidth]);

  const getSlideComponents = useCallback(() => {
    // If there are only two slides, we pass in four slides (two of each) to
    // allow the slider to show Next and Previous buttons on all slides
    const slidesToShow = slides.length === 2 ? [...slides, ...slides] : slides;
    let customSlideWidth;

    if (slideWidth === 100 && isMobileMaterialSwatches) {
      customSlideWidth = '100%';
    } else if (windowWidth >= 767 && windowWidth < 1024) {
      customSlideWidth = '100%';
    } else {
      customSlideWidth = slideWidth;
    }
    // For nuka-carousel v8 (new consumer app), visible slides are determined by
    // slide width; use full-width slides (100%) so one image fills the view
    // and zoom applies to the whole visible area (no next-image peek).
    const slideWrapperClass = '!min-w-full w-full shrink-0';

    const isDuplicated = slidesToShow.length > slides.length;

    return slidesToShow.map((slide, slideIndex) => {
      const isSlideSelected = slideIndex === selectedSlideIndex;
      const imageSrc = `${slide.url}?${queryString.stringify(slide.imgProps)}`;
      // Unique key without array index: when duplicated (2→4 slides), use suffix to distinguish copies
      const slideKey = isDuplicated
        ? `${imageSrc}-${slideIndex < slides.length ? 'orig' : 'copy'}`
        : imageSrc;
      // When wrapped, image fills the slide (100%); otherwise use customSlideWidth
      const imageWidth = slideWrapperClass ? '100%' : customSlideWidth;
      const slideContent = (
        <InnerImageZoom
          className={classNames('!h-[300px]', slide.classname)}
          width={imageWidth}
          height={300}
          src={imageSrc}
          zoomSrc={`${slide.url}?${queryString.stringify({
            ...slide.imgProps,
            ...slide.zoomImgProps,
          })}`}
          zoomType={
            isSlideSelected ? 'hover' : 'click'
          } /* zoomType is being set to click for all non-active slides to not trigger a zoom when the user is trying to click the other slides. */
          zoomPreload
          hasSpacer
          hideCloseButton
          hideHint
          afterZoomIn={() => setIsZoomIn(true)}
          afterZoomOut={() => setIsZoomIn(false)}
        />
      );

      return (
        <div key={slideKey} className={slideWrapperClass}>
          {slideContent}
        </div>
      );
    });
  }, [
    slides,
    selectedSlideIndex,
    slideWidth,
    isMobileMaterialSwatches,
    windowWidth,
  ]);

  const getEmblaSlideComponents = useCallback(() => {
    const isSingleSlide = slides.length === 1;
    const slideWrapperClass = isSingleSlide
      ? 'basis-full'
      : 'basis-[80%] pl-0 !px-0.5';

    const slidesToShow =
      !isSingleSlide && slides.length < 3
        ? [...slides, ...slides, ...slides]
        : slides;
    const isDuplicated = slidesToShow.length > slides.length;

    return slidesToShow.map((slide, slideIndex) => {
      const isSlideSelected = slideIndex === selectedSlideIndex;
      const imageSrc = `${slide.url}?${queryString.stringify(slide.imgProps)}`;
      const slideKey = isDuplicated ? `${imageSrc}-${slideIndex}` : imageSrc;
      const imageWidth = '100%';
      const slideContent = (
        <InnerImageZoom
          className={classNames('!h-[300px]', slide.classname)}
          width={imageWidth}
          height={300}
          src={imageSrc}
          zoomSrc={`${slide.url}?${queryString.stringify({
            ...slide.imgProps,
            ...slide.zoomImgProps,
          })}`}
          zoomType={
            isSlideSelected ? 'hover' : 'click'
          } /* zoomType is being set to click for all non-active slides to not trigger a zoom when the user is trying to click the other slides. */
          zoomPreload
          hasSpacer
          hideCloseButton
          hideHint
          afterZoomIn={() => setIsZoomIn(true)}
          afterZoomOut={() => setIsZoomIn(false)}
        />
      );

      return (
        <EmblaSlide key={slideKey} className={slideWrapperClass}>
          {slideContent}
        </EmblaSlide>
      );
    });
  }, [selectedSlideIndex, slides]);

  const slideComponents = isNewConsumerApp
    ? getEmblaSlideComponents()
    : getSlideComponents();

  const getCurrentSlideIndex = useCallback(() => {
    if (isNewConsumerApp && slides.length === 2 && selectedSlideIndex > 1) {
      return (selectedSlideIndex % slides.length) + 1;
    }

    if (slides.length === 2 && selectedSlideIndex > 1) {
      // If there are only 2 slides and current slide is index 2 or 3, we want
      // to return 1 or 2 respectively, because we want to give the impression
      // to the user that there are only two slides even though we are
      // rendering 4 slides in the carousel.
      return selectedSlideIndex - 1;
    }

    return selectedSlideIndex + 1;
  }, [selectedSlideIndex, slides.length]);

  const currentSlideIndex = getCurrentSlideIndex();

  return isDesktop ? (
    // We use a custom className here to support overriding nested third party
    // styles in globals-tailwind.css
    <div className="FabricGallery-desktop-slider relative min-h-[300px]">
      <ImageSliderStandard
        config={{
          children: slideComponents,
          sliderConfig: {
            visibleSlides: 1.2,
            deviceOverride: true,
            afterSlideFn: setSelectedSlideIndex,
            sliderIndex: selectedSlideIndex,
            fullHeight: true,
          },
          variation: { value: 'Component' },
        }}
        fabricGridControls
        fullWidth
        disableSliding={isZoomIn}
        variant="material-swatches-sidebar"
        isMobileMaterialSwatches
      />
      {topRightSide && topRightSide}
      {!!slides.length && (
        <>
          <div className="absolute bottom-4 left-[calc(10%_+_1rem)] w-fit">
            <div className="text-sm font-bold bg-white rounded-sm text-gray min-w-[43px] pt-px pr-1 pb-px pl-1.5 pointer-events-none text-center right-0 bottom-0">
              {`${currentSlideIndex} / ${slides.length}`}
            </div>
          </div>
          <div className="absolute bottom-4 right-[calc(10%_+_1rem)] w-fit">
            {bottomRightSide}
          </div>
        </>
      )}
    </div>
  ) : isMobileMaterialSwatches ? (
    <div className="relative min-h-[300px] [&_.iiz\_\_img--abs]:object-cover [&_.iiz\_\_img--abs]:object-right-bottom">
      <ImageSliderStandard
        config={{
          children: slideComponents,
          sliderConfig: {
            visibleSlides: 1.2,
            deviceOverride: true,
            afterSlideFn: setSelectedSlideIndex,
            navType: '',
          },
          variation: { value: 'Component' },
        }}
        disableSliding={isZoomIn}
        fabricGridControls
        fullWidth
        isMobileMaterialSwatches={isMobileMaterialSwatches}
        variant="material-swatches-sidebar"
      />
      {topRightSide && topRightSide}
      {!!slides.length && (
        <>
          <div className="absolute bottom-4 left-[calc(10%_+_1rem)] w-fit">
            <div className="text-sm font-bold rounded-sm text-gray bg-white min-w-[43px] pt-px pr-1 pb-px pl-1.5 pointer-events-none text-center right-0 bottom-0">
              {`${currentSlideIndex} / ${slides.length}`}
            </div>
          </div>

          <div className="absolute bottom-4 right-[calc(10%_+_1rem)] w-fit">
            {bottomRightSide}
          </div>
        </>
      )}
    </div>
  ) : (
    <div />
  );
};

FabricGallery.propTypes = {
  bottomRightSide: PropTypes.node,
  isMobileMaterialSwatches: PropTypes.bool,
  slides: PropTypes.arrayOf(PropTypes.string),
  topRightSide: PropTypes.node,
};

export default FabricGallery;
